import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import SessionManager from '../routes/SessionManager';
import Toastr from 'toastr';
import Loading from '../components/Loading';
import LocationCard from '../components/dashboard/LocationCard';
import LocationMap from '../components/dashboard/LocationMap';
import ImageResize from '../components/common/ImageResize';
import { useJsApiLoader } from '@react-google-maps/api';
import '../assets/css/custom-new.css';
import CommonUtils from '../util/CommonUtils';
import API from '../service/SiteKickAPIService';

const CustomerStreams = () => {
    let sessionManager = new SessionManager();
    const { token: streamToken } = useParams();

    if (!streamToken) {
        Toastr.error('Stream token is missing in URL. Please contact support.');
        return <div className="m-5"><div className="alert alert-danger">Stream token is missing in URL. Please contact support.</div></div>;
    }

    // Initialize user from session or create a basic user object for stream token access
    // Note: user_login and user_token will be updated after fetching customer
    const initialUser = sessionManager.readCookie('user', false) || {
        stream_token: streamToken,
        user_login: `stream.7777777777777`, // Use stream token for initial auth
        user_token: 'sitekick'
    };

    const [customer, setCustomer] = useState(null);
    const [user, setUser] = useState(initialUser);
    const [site_selected, setSiteSelected] = useState(null);
    const [zones, setZones] = useState([]);
    const [leadershipZones, setLeadershipZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState(null);
    const [locationData, setLocationData] = useState({});
    const [site_locations, setSiteLocations] = useState(null);
    const [videoContainerHeight, setVideoContainerHeight] = useState(450);
    const videoContainerRef = React.useRef(null);

    // Load Google Maps API with same ID as GoogleMapForSiteSupt to avoid conflicts
    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: 'AIzaSyD7rYafEbst83cps65IrvmUz61n33z4kIM'
    });

    // Combine filtered zones for map display - one marker per site with working camera
    const allMapLocations = useMemo(() => {
        const mapLocs = [];

        if (!site_locations) return mapLocs;

        // Use leadership zones or regular zones depending on customer config
        const displayZones = customer && customer.leadership_sites ? leadershipZones : zones;

        // Group zones by site_id
        const zonesBySite = {};
        displayZones.forEach(zone => {
            if (!zonesBySite[zone.site_id]) {
                zonesBySite[zone.site_id] = [];
            }
            zonesBySite[zone.site_id].push(zone);
        });

        // For each site, show marker with first zone (working or not)
        Object.keys(zonesBySite).forEach(siteId => {
            const site = site_locations.find(s => s.site_id.toString() === siteId.toString());
            if (site && site.site_geolocation) {
                const coords = site.site_geolocation.split(',');
                if (coords.length === 2) {
                    const lat = parseFloat(coords[0]);
                    const lng = parseFloat(coords[1]);
                    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
                        // Find first working camera: online and streaming capable
                        const workingZone = zonesBySite[siteId].find(zone => {
                            const isConnected = zone.location_operstatus === 'Connected';
                            const canStream = zone.device_id && (
                                (zone.location_features && zone.location_features.includes('s1')) ||
                                ['axis', 'uniview'].includes(zone.location_device_type)
                            );
                            return isConnected && canStream;
                        });

                        // Use first working camera, or fall back to first zone if none working
                        const zoneToShow = workingZone || zonesBySite[siteId][0];

                        // Check if this zone is working
                        const isConnected = zoneToShow.location_operstatus === 'Connected';
                        const canStream = zoneToShow.device_id && (
                            (zoneToShow.location_features && zoneToShow.location_features.includes('s1')) ||
                            ['axis', 'uniview'].includes(zoneToShow.location_device_type)
                        );
                        const isWorking = isConnected && canStream;

                        mapLocs.push({
                            location_id: zoneToShow.location_id,
                            location_name: site.site_name, // Use site name as marker name
                            location_lat: lat,
                            location_lng: lng,
                            location_operstatus: zoneToShow.location_operstatus,
                            device_id: zoneToShow.device_id,
                            location_features: zoneToShow.location_features,
                            location_device_type: zoneToShow.location_device_type,
                            site_name: site.site_name,
                            zone_name: zoneToShow.location_name,
                            is_working: isWorking, // Flag for marker color
                            markerType: 'zone',
                            sourceObject: zoneToShow
                        });
                    }
                }
            }
        });

        return mapLocs;
    }, [zones, leadershipZones, customer, site_locations]);

    // if (!userObj["zone_prefs_for_sites"]) {
    //     for (var index = 0; index < userObj.user_sites.length; index++) {
    //         var user_site = userObj.user_sites[index];
    //         userObj["zone_prefs_for_sites"] = { ...userObj["zone_prefs_for_sites"], [user_site]: userObj.zone_prefs }
    //     }
    // }

    useEffect(() => {
        // Fetch customer first, then process sites
        const initializeData = async () => {
            sessionManager.storeCookie('user', user); // Store initial user with stream token for API calls in fetchCustomer    
            const _c = await fetchCustomer();

            if (!_c || !_c.stream_token) {
                Toastr.error('Customer information is missing to stream. Please contact support.');
                return;
            }

            // Check if leadership sites are configured
            if (!_c.leadership_sites || Object.keys(_c.leadership_sites).length === 0) {
                Toastr.error('No sites configured to stream. Please contact support.');
                return;
            }

            // Find first site with zones configured
            let selectedSiteId = null;
            let selectedZoneIds = [];

            for (let siteId of Object.keys(_c.leadership_sites)) {
                const zoneIds = _c.leadership_sites[siteId];
                if (zoneIds && zoneIds.length > 0) {
                    selectedSiteId = siteId;
                    selectedZoneIds = zoneIds;
                    break;
                }
            }

            if (!selectedSiteId || selectedZoneIds.length === 0) {
                Toastr.error('No zones configured for streaming. Please contact support.');
                return;
            }

            console.log('Selected site for streaming:', selectedSiteId, 'with zones:', selectedZoneIds);

            // Update user object with customer info
            const updatedUser = {
                ...user,
                customer_id: _c.customer_id,
                user_login: `stream.${_c.customer_id}`, // Format: stream.<customer_id>
                user_token: 'sitekick',
                user_sites: Object.keys(_c.leadership_sites), // ALL leadership sites for map and streaming
                selected_leadership_site: selectedSiteId,
                selected_leadership_zones: selectedZoneIds,
                zone_prefs: _c.zone_prefs || []
            };
            setUser(updatedUser);
            sessionManager.storeCookie('user', updatedUser);

            // Fetch all sites to populate site_locations (needed for map to show site information)
            await fetchSiteLocations(updatedUser);
            // fetchLeadershipZones will be triggered by the customer useEffect below
        };

        initializeData();
    }, []);

    useEffect(() => {
        // Only fetch zones for selected site if NOT using leadership sites
        if (site_selected !== null && (!customer || !customer.leadership_sites)) {
            fetchSiteLocationZones();
        }
    }, [site_selected]);

    useEffect(() => {
        // Fetch leadership zones when customer with leadership_sites is loaded
        if (customer && customer.leadership_sites && user) {
            fetchLeadershipZones();
        }
    }, [customer, user]);

    // Observe video container height changes
    useEffect(() => {
        if (!videoContainerRef.current) return;

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                const height = entry.contentRect.height;
                if (height > 0) {
                    setVideoContainerHeight(height);
                }
            }
        });

        resizeObserver.observe(videoContainerRef.current);

        return () => resizeObserver.disconnect();
    }, [selectedZone]);

    const getDefaultSite = (site_locations) => {
        if (site_locations && site_locations.length > 0) {
            for (let site of site_locations) {
                if (user.site_selected && user.site_selected.site_id === site.site_id) {
                    return site;
                }
            }
            return site_locations[0];
        }
        return null;
    };

    const fetchCustomer = async () => {
        let _c = await CommonUtils.getCustomerByStreamToken(streamToken, user);
        setCustomer(_c);
        return _c;
    };

    let fetchSiteLocations = (userObj = user) => {
        return new Promise((resolve, reject) => {

            let criteria = { "from": 0, "size": 1000 };
            criteria.advanced_filter = [];

            for (let siteId of userObj.user_sites)
                criteria.advanced_filter.push({ attribute: 'site_id', virtual_column: true, value: siteId, operator: '=', join_by: 'or' });

            CommonUtils.ajax(
                {
                    url: API.COMMON_TABLE_OPERATIONS.SITE.FETCH,
                    type: 'POST',
                    contentType: "application/json; charset=utf-8",
                    dataType: "json",
                    data: JSON.stringify({
                        "criteria": criteria,
                        "user_type": "user",
                        user_login: user.user_login,
                        user_token: user.user_token,
                        app_name: 'SITESUPT'
                    })
                }
            ).then(
                (res) => {
                    if (res.status && res.status.code === 200) {
                        let site_locations = [];
                        let records = res.data.records || [];
                        for (let r of records)
                            if (r.site_state === 'Active') // Only include active sites
                                site_locations.push({ ...r.record });

                        let loc = 'site_name';
                        site_locations = site_locations.sort((a, b) => a[loc] > b[loc] ? 1 : a[loc] < b[loc] ? -1 : 0);

                        let _user = JSON.parse(JSON.stringify(userObj));
                        _user.site_locations = site_locations;

                        if (site_locations && site_locations.length > 0) {
                            let siteDefault = getDefaultSite(site_locations);
                            if (siteDefault) {
                                _user.site_selected = siteDefault;
                                sessionManager.storeCookie('user', _user);
                            }
                            setUser(_user);
                            setSiteLocations(site_locations);
                            setSiteSelected(_user.site_selected);
                            resolve(site_locations);
                        } else {
                            sessionManager.storeCookie('user', userObj);
                            Toastr.error('No Sites Found. Please contact support.');
                            reject('No sites found');
                        }
                    } else {
                        reject('Failed to fetch sites');
                    }
                },
                (error) => {
                    reject(error);
                });
        });
    }

    let fetchSiteLocationZones = async () => {
        var data = {
            customer_id: site_selected.customer_id,
            site_id: site_selected.site_id,
            user_login: user.user_login,
            user_token: user.user_token
        }

        CommonUtils.ajax(
            {
                url: API.LOCATION.BASE,
                type: 'POST',
                contentType: "application/json; charset=utf-8",
                dataType: "json",
                data: JSON.stringify(data)
            }
        ).then(
            (res) => {
                if (res.status && res.status.code === 200) {
                    let _zones = res.data || [];
                    setZones(_zones);

                    console.log('Fetched Zones for Site:', site_selected.site_name, _zones);

                    // Set first zone as selected by default
                    if (_zones.length > 0) {
                        setSelectedZone(_zones[0]);
                    }
                }
            },
            (res) => {
            });
    }

    const fetchLeadershipZones = async () => {
        console.log('Fetching leadership zones for customer:', customer.customer_id);
        console.log('Leadership sites:', customer.leadership_sites);

        // Fetch zones from ALL leadership sites for the map
        const allZonesPromises = [];
        for (let siteId of Object.keys(customer.leadership_sites)) {
            if (user.site_locations && user.site_locations.find(s => s.site_id.toString() === siteId.toString()) === undefined)
                continue;

            const zoneIds = customer.leadership_sites[siteId];

            if (!zoneIds || zoneIds.length === 0) {
                console.log('Site', siteId, 'has no zones configured, skipping');
                continue;
            }

            console.log('Fetching zones for site:', siteId, 'zone IDs:', zoneIds);

            const data = {
                customer_id: customer.customer_id,
                site_id: siteId,
                user_login: user.user_login,
                user_token: user.user_token
            };

            const promise = CommonUtils.ajax({
                url: API.LOCATION.BASE,
                type: 'POST',
                contentType: "application/json; charset=utf-8",
                dataType: "json",
                data: JSON.stringify(data)
            }).then(res => {
                if (res.status && res.status.code === 200) {
                    let allLocations = res.data || [];
                    // Filter only the zones specified in leadership_sites for this site
                    let filteredZones = allLocations.filter(loc => zoneIds.includes(loc.location_id));
                    console.log(`Site ${siteId}: fetched ${filteredZones.length} zones`);
                    return filteredZones;
                }
                return [];
            }).catch(error => {
                console.error('Error fetching locations for site', siteId, error);
                return [];
            });

            allZonesPromises.push(promise);
        }

        // Wait for all zones to be fetched
        const allZoneArrays = await Promise.all(allZonesPromises);
        const allZones = allZoneArrays.flat(); // Flatten all zones into one array

        // Sort by zone_prefs if available
        if (customer.zone_prefs && allZones.length > 0) {
            allZones.sort((x, y) => customer.zone_prefs.indexOf(x.location_id) - customer.zone_prefs.indexOf(y.location_id));
            console.log('Sorted by zone_prefs');
        }

        console.log('Total leadership zones fetched:', allZones.length, allZones);
        setLeadershipZones(allZones);

        // Set first zone as selected by default
        if (allZones.length > 0 && !selectedZone) {
            console.log('Setting default selected zone:', allZones[0].location_name);
            setSelectedZone(allZones[0]);
        }
    };

    const onFeatureStream = (site, location) => {
        console.log('onFeatureStream called:', {
            currentSelected: selectedZone && selectedZone.location_id,
            newLocation: location && location.location_id,
            locationName: location && location.location_name
        });
        setSelectedZone(prev => {
            const shouldToggleOff = prev && prev.location_id === location.location_id;
            console.log('Setting selectedZone to:', shouldToggleOff ? null : (location && location.location_name));
            return shouldToggleOff ? null : location;
        });
    };

    const onLocationLoaded = (location, site) => {
        setLocationData(prev => ({ ...prev, [location.location_id]: { location, site } }));
    };

    if (!user) {
        return <Loading />;
    }

    // Show error if no leadership sites configured
    if (customer && (!customer.leadership_sites || Object.keys(customer.leadership_sites).length === 0)) {
        return (
            <div className="m-5">
                <div className="alert alert-warning">
                    <h5>No Sites Configured to Stream</h5>
                    <p>This customer account does not have any sites configured for streaming. Please contact support to set up leadership sites.</p>
                </div>
            </div>
        );
    }

    if (!site_selected) {
        return <Loading />;
    }

    return (
        <React.Fragment>
            <style>{`
                .map-marker-label {
                    text-shadow:
                        -1px -1px 2px white,
                        1px -1px 2px white,
                        -1px 1px 2px white,
                        1px 1px 2px white,
                        0 0 4px white,
                        0 0 8px white !important;
                    font-weight: bold !important;
                    transform: translateY(20px) !important;
                    display: block !important;
                }
            `}</style>
            <div className="m-5" >
                <div className="border-rounded p-2" style={{
                    /* From https://css.glass */
                    background: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '4px',
                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                    backdropFilter: 'blur(5px)',
                    '-webkit-backdrop-filter': 'blur(5px)',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                }}>
                    {/* Customer Logo Header */}
                    {customer && customer.customer_logo && (
                        <div className="row mb-3">
                            <div className="col-12">
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: '12px 16px',
                                    backgroundColor: '#fff',
                                    borderRadius: '4px',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                            src={(() => {
                                                const parts = customer.customer_logo.split('/');
                                                const bucket = parts[0];
                                                const key = parts.slice(1).join('/');
                                                return `https://${bucket}.s3.amazonaws.com/${key}`;
                                            })()}
                                            height="36px"
                                            alt={customer.customer_name || 'Customer Logo'}
                                        />
                                        {customer.customer_name && (
                                            <h6 style={{
                                                margin: 0,
                                                marginLeft: '16px',
                                                color: '#333',
                                                fontWeight: 600
                                            }}>
                                                {customer.customer_name} Live Stream
                                            </h6>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <h6 style={{ fontSize: '12px', color: '#666', fontWeight: 600 }}>Powered by</h6>
                                        <svg version="1.0" xmlns="http://www.w3.org/2000/svg" height="30px" viewBox="0 0 534.000000 154.000000" preserveAspectRatio="xMidYMid meet">
                                            <g transform="translate(0.000000,154.000000) scale(0.100000,-0.100000)" fill="#6b32b4" stroke="none">
                                                <path d="M193 1414 c-18 -9 -33 -19 -33 -23 0 -3 196 -202 435 -441 l435 -435 0 458 0 457 -402 0 c-349 0 -407 -3 -435 -16z" />
                                                <path d="M1070 970 c0 -253 3 -460 8 -460 4 0 81 73 170 163 l163 164 -3 235 -3 236 -30 43 c-47 65 -82 79 -202 79 l-103 0 0 -460z" />
                                                <path d="M108 1328 c-10 -18 -14 -122 -16 -435 l-3 -413 458 0 458 0 -435 435 c-239 239 -438 435 -442 435 -4 0 -13 -10 -20 -22z" />
                                                <path d="M1750 1039 c-83 -13 -138 -70 -138 -141 0 -69 61 -125 178 -163 68 -22 105 -53 105 -89 0 -44 -34 -69 -90 -70 -58 -1 -101 23 -116 65 -10 26 -16 29 -55 29 l-44 0 7 -32 c18 -82 97 -133 206 -132 112 0 180 51 181 135 2 87 -40 129 -173 174 -40 14 -81 32 -91 42 -25 22 -23 68 4 93 46 43 168 14 168 -41 0 -16 7 -19 45 -19 40 0 45 2 45 23 0 35 -36 85 -77 105 -48 24 -95 31 -155 21z" />
                                                <path d="M4430 1040 c-115 -21 -172 -107 -173 -259 0 -105 14 -160 55 -207 45 -52 83 -68 159 -68 86 0 152 35 180 97 30 67 26 77 -26 77 -39 0 -45 -3 -45 -21 0 -11 -13 -34 -29 -50 -25 -24 -38 -29 -79 -29 -93 0 -126 53 -125 201 1 134 40 189 134 189 31 0 47 -7 70 -29 16 -16 29 -39 29 -50 0 -18 6 -21 45 -21 53 0 56 10 24 77 -34 71 -126 110 -219 93z" />
                                                <path d="M2150 775 l0 -265 45 0 45 0 0 265 0 265 -45 0 -45 0 0 -265z" />
                                                <path d="M2380 1005 l0 -35 80 0 80 0 0 -230 0 -230 45 0 45 0 2 228 3 227 83 3 82 3 0 34 0 35 -210 0 -210 0 0 -35z" />
                                                <path d="M2950 775 l0 -265 170 0 170 0 0 35 0 35 -125 0 -125 0 0 85 0 85 105 0 105 0 0 35 0 35 -105 0 -105 0 0 75 0 75 125 0 125 0 0 35 0 35 -170 0 -170 0 0 -265z" />
                                                <path d="M3440 775 l0 -265 45 0 45 0 0 82 c0 78 1 83 32 115 l31 32 80 -114 80 -115 54 0 54 0 -45 63 c-25 34 -72 100 -104 147 l-60 86 100 117 99 117 -55 0 -55 0 -103 -122 -103 -123 -3 123 -3 122 -44 0 -45 0 0 -265z" />
                                                <path d="M3990 775 l0 -265 45 0 45 0 0 265 0 265 -45 0 -45 0 0 -265z" />
                                                <path d="M4830 775 l0 -265 45 0 45 0 0 83 c0 81 1 84 33 115 l32 31 80 -114 79 -115 54 0 53 0 -19 28 c-11 15 -54 76 -96 135 -43 60 -80 115 -83 123 -4 12 67 105 171 222 l19 22 -55 0 -56 0 -99 -119 c-54 -66 -101 -120 -105 -120 -5 -1 -8 53 -8 119 l0 120 -45 0 -45 0 0 -265z" />
                                                <path d="M1255 630 l-150 -150 153 0 152 0 0 150 c0 83 -1 150 -3 150 -1 0 -70 -67 -152 -150z" />
                                                <path d="M90 358 c0 -93 19 -145 66 -190 48 -44 71 -48 312 -48 l227 0 160 160 160 160 -463 0 -462 0 0 -82z" />
                                                <path d="M1233 313 c122 -122 129 -127 143 -108 23 30 34 84 34 163 l0 72 -152 0 -153 0 128 -127z" />
                                                <path d="M1070 265 l0 -145 95 0 c63 0 108 5 132 15 l37 16 -129 129 c-71 72 -131 130 -132 130 -2 0 -3 -65 -3 -145z" />
                                                <path d="M885 260 l-140 -140 143 0 142 0 0 140 c0 77 -1 140 -3 140 -1 0 -65 -63 -142 -140z" />
                                            </g>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="row" style={{ alignItems: 'stretch' }}>

                        <div className="col-lg-6">
                            <div style={{ height: `${videoContainerHeight}px` }}>
                                {isLoaded ? (
                                    <LocationMap
                                        locations={allMapLocations}
                                        selectedLocationId={selectedZone ? selectedZone.location_id : null}
                                        onLocationSelect={(locationId) => {
                                            if (locationId) {
                                                // Use leadershipZones if customer.leadership_sites exists, otherwise use regular zones
                                                const displayZones = customer && customer.leadership_sites ? leadershipZones : zones;
                                                const selected = displayZones.find(z => z.location_id === locationId);
                                                setSelectedZone(selected || null);
                                            } else {
                                                setSelectedZone(null);
                                            }
                                        }}
                                        mapHeight={`${videoContainerHeight}px`}
                                        readOnly={false}
                                    />
                                ) : (
                                    <div style={{
                                        height: `${videoContainerHeight}px`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: '4px'
                                    }}>
                                        <span style={{ color: '#999' }}>Loading map...</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div ref={videoContainerRef} style={{ minHeight: '450px' }}>
                                {selectedZone ? (
                                    <LocationCard
                                        key={selectedZone.location_id}
                                        site_id={selectedZone.site_id}
                                        location_id={selectedZone.location_id}
                                        onFeatureStream={onFeatureStream}
                                        onLocationLoaded={onLocationLoaded}
                                        isFeatured={true}
                                        playInTile={true}
                                        compact={true}
                                    />
                                ) : (
                                    <div className="card bg-shadow" style={{ marginBottom: 0, height: '100%' }}>
                                        <div className="card-body" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                                            <span>Select a zone to play video</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 mt-5 p-2 bg-white border-rounded" style={{
                            paddingLeft: '12px',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            {(() => {
                                // Use leadershipZones if customer.leadership_sites exists, otherwise use regular zones
                                const displayZones = customer && customer.leadership_sites ? leadershipZones : zones;

                                if (!displayZones || displayZones.length === 0) {
                                    return (
                                        <div className="text-center p-3">
                                            <div className="alert alert-info">
                                                {customer && customer.leadership_sites
                                                    ? 'Loading zones from leadership sites...'
                                                    : 'No zones available'}
                                            </div>
                                        </div>
                                    );
                                }

                                return (
                                    <div style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: '6px',
                                        overflowY: 'auto',
                                        flex: 1,
                                        paddingRight: '8px'
                                    }}>
                                        {displayZones.map(zone => {
                                            const isFeatured = selectedZone && selectedZone.location_id === zone.location_id;
                                            if (zone.location_name === 'North PTZ' || (selectedZone && selectedZone.location_name === 'North PTZ')) {
                                                console.log('North PTZ check:', {
                                                    zoneName: zone.location_name,
                                                    zoneId: zone.location_id,
                                                    selectedZoneId: selectedZone && selectedZone.location_id,
                                                    selectedZoneName: selectedZone && selectedZone.location_name,
                                                    isFeatured
                                                });
                                            }

                                            const locData = locationData[zone.location_id];
                                            const snapImage = locData
                                                ? `https://sitesupt-location-data.s3.amazonaws.com/cusr_${locData.location.customer_id}/site_${locData.location.site_id}/location_${zone.location_id}/image_${locData.location.device_id}_${locData.location.location_last_pic_pic_time}.jpg`
                                                : null;

                                            // Check if camera is working (online + streaming capable)
                                            const isConnected = zone.location_operstatus === 'Connected';
                                            const canStream = zone.device_id && (
                                                (zone.location_features && zone.location_features.includes('s1')) ||
                                                ['axis', 'uniview'].includes(zone.location_device_type)
                                            );
                                            const isWorking = isConnected && canStream;
                                            const statusColor = isWorking ? 'rgba(107, 50, 180)' : '#6c757d'; // Green if working, gray if not

                                            const currSite = site_locations ? site_locations.find(s => s.site_id === zone.site_id) : null;
                                            return (
                                                <div key={zone.location_id} style={{ flex: '0 0 calc(15% - 3px)', minWidth: '130px' }}>
                                                    {isFeatured ? (
                                                        // Show placeholder when featured
                                                        <div
                                                            onClick={() => onFeatureStream(null, zone)}
                                                            style={{
                                                                position: 'relative',
                                                                aspectRatio: '16/10',
                                                                borderRadius: '4px',
                                                                overflow: 'hidden',
                                                                border: `2px solid ${statusColor}`,
                                                                boxShadow: isWorking
                                                                    ? '0 0 0 2px rgba(40,167,69,0.3), 0 4px 12px rgba(40,167,69,0.25)'
                                                                    : '0 0 0 2px rgba(108,117,125,0.3), 0 4px 12px rgba(108,117,125,0.25)',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            {snapImage && (
                                                                <ImageResize
                                                                    src={snapImage}
                                                                    resize_perc="0.2"
                                                                    alt=""
                                                                    style={{
                                                                        position: 'absolute',
                                                                        inset: 0,
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover',
                                                                        opacity: 0.55,
                                                                    }}
                                                                />
                                                            )}
                                                            <div style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                background: isWorking ? 'rgba(107, 50, 180, 0.2)' : 'rgba(108,117,125,0.2)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                            }}>
                                                                <span style={{
                                                                    background: statusColor,
                                                                    color: '#fff',
                                                                    fontSize: '8px',
                                                                    fontWeight: 800,
                                                                    padding: '2px 7px',
                                                                    borderRadius: '4px',
                                                                    letterSpacing: '1px',
                                                                }}>▶ SPOTLIGHT</span>
                                                            </div>
                                                            {locData && (
                                                                <div style={{
                                                                    position: 'absolute',
                                                                    bottom: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    padding: '4px 6px',
                                                                    color: '#fff',
                                                                    fontSize: '12px',
                                                                    whiteSpace: 'nowrap',
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 2px 8px rgba(0,0,0,0.8)'
                                                                }}>
                                                                    <div>{locData.location.location_name}</div>
                                                                    <div>{currSite.site_name ? `${currSite.site_name}` : ''}</div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        // Show snapshot tile when not featured
                                                        <LocationCard
                                                            site_id={zone.site_id}
                                                            location_id={zone.location_id}
                                                            onFeatureStream={onFeatureStream}
                                                            onLocationLoaded={onLocationLoaded}
                                                            isFeatured={false}
                                                            playInTile={false}
                                                            compact={true}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default CustomerStreams;
